import { useEffect, useState, useCallback, useMemo } from "react";
import { Button, Space, Table, TableProps, Tag } from "antd";
import { DDRDisplayData } from "../../types/DDRDisplayData";
import {
    fetchAllStagingPlodsByAid,
    fetchStagingPlodsByAid,
} from "../../backendInteraction/ApiFetch";
import { StagingTitelineAppPlod } from "../../types/StagingTitelineAppPlod";
import { useNavigate } from "react-router-dom";
import notLoginState from "../../asserts/notLoginState.png";
import { fetchTotalActivityHours } from "../../backendInteraction/ActivitiesApiFetch";
import "./SubmittedDDR.css";

export const SubmittedDDR = () => {
    const [aid, setAid] = useState<number>(() => {
        const storedUser = localStorage.getItem("DORS_USER");
        return storedUser ? JSON.parse(storedUser).Aid : 0;
    });

    const [authorityLv, setAuthorityLv] = useState<string>(() => {
        const storedUser = localStorage.getItem("DORS_USER");
        return storedUser ? JSON.parse(storedUser).AuthorityLv : "";
    });

    const [data, setData] = useState<DDRDisplayData[] | null>(null);

    const navigate = useNavigate();

    const safeString = (value: any) => (!value || value === "null" ? "" : value);

    const transformPlodsToDDRDisplay = useCallback(
        async (plods: StagingTitelineAppPlod[]): Promise<DDRDisplayData[]> => {
            return await Promise.all(
                plods.map(async (plod) => {
                    const date = safeString(plod.PlodDate);
                    const shift = safeString(plod.PlodShift);
                    const creation_date = date && shift ? `${date}-${shift}` : "";

                    let totalHours = 0;
                    const rawHours = plod.TotalActivityHrs;

                    if (isNaN(Number(rawHours)) || Number(rawHours) === 0) {
                        totalHours = await fetchTotalActivityHours(plod.Pid ?? -1);
                    } else {
                        totalHours = parseFloat(Number(rawHours).toFixed(2));
                    }

                    return {
                        key: plod.Pid,
                        pid: plod.Pid,
                        contract_no: safeString(plod.ContractNo),
                        date,
                        shift,
                        creation_date,
                        rig_no: safeString(plod.RigNo),
                        report_state: parseInt(plod.ReportState) || -1,
                        department: safeString(plod.Department),
                        location: safeString(plod.Location),
                        mach_hrs_from: safeString(plod.MachineHoursFrom),
                        mach_hrs_to: safeString(plod.MachineHoursTo),
                        total_activity_hours: totalHours,
                        comments: safeString(plod.Comments),
                    };
                })
            );
        },
        []
    );

    const loadData = useCallback(async () => {
        try {
            let fetchedData: StagingTitelineAppPlod[] = [];

            if (authorityLv === "C") {
                fetchedData = (await fetchStagingPlodsByAid(aid, false)) ?? [];
            } else {
                fetchedData = (await fetchAllStagingPlodsByAid(aid)) ?? [];
            }

            const transformed = await transformPlodsToDDRDisplay(fetchedData);
            setData(transformed);
        } catch (error) {
            console.error("Error loading data from cloud server:", error);
        }
    }, [aid, authorityLv, transformPlodsToDDRDisplay]);

    const handleEditing = (record: DDRDisplayData) => {
        navigate("/view-ddr", { state: { ddrData: record } });
    };

    useEffect(() => {
        if (aid !== 0) {
            loadData();
        }
    }, [aid, loadData]);

    const unique = (arr: (string | number | undefined | null)[]) =>
        Array.from(
            new Set(
                arr.filter(
                    (x): x is string | number =>
                        x !== undefined && x !== null && String(x).trim() !== ""
                )
            )
        );

    const getReportStateLabel = (state: number) => {
        if (state > 4) return "Preparing for Reporting";
        if (state % 2 === 1)
            return state === 1
                ? "Waiting for supervisor approval"
                : "Waiting for client approval";
        return state === 2 ? "Driller rechecking" : "Supervisor rechecking";
    };

    const contractFilters = useMemo(
        () =>
            unique((data ?? []).map((d) => d.contract_no)).map((v) => ({
                text: String(v),
                value: String(v),
            })),
        [data]
    );

    const dateFilters = useMemo(
        () =>
            unique((data ?? []).map((d) => d.creation_date)).map((v) => ({
                text: String(v),
                value: String(v),
            })),
        [data]
    );

    const rigFilters = useMemo(
        () =>
            unique((data ?? []).map((d) => d.rig_no)).map((v) => ({
                text: String(v),
                value: String(v),
            })),
        [data]
    );

    const reportStateFilters = useMemo(() => {
        const labels = unique((data ?? []).map((d) => getReportStateLabel(d.report_state)));
        return labels.map((v) => ({ text: String(v), value: String(v) }));
    }, [data]);

    const columns: TableProps<DDRDisplayData>["columns"] = [
        {
            title: "Contract Number",
            dataIndex: "contract_no",
            key: "contract_no",
            filters: contractFilters,
            filterSearch: true,
            onFilter: (value, record) =>
                String(record.contract_no) === String(value),
            render: (text) => <span className="contract_no_text">{text}</span>,
        },
        {
            title: "Plod Date",
            dataIndex: "creation_date",
            key: "creation_date",
            filters: dateFilters,
            filterSearch: true,
            onFilter: (value, record) =>
                String(record.creation_date) === String(value),
        },
        {
            title: "Rig Number",
            dataIndex: "rig_no",
            key: "rig_no",
            filters: rigFilters,
            filterSearch: true,
            onFilter: (value, record) => String(record.rig_no) === String(value),
        },
        {
            title: "Report State",
            key: "report_state",
            dataIndex: "report_state",
            filters: reportStateFilters,
            filterSearch: true,
            onFilter: (value, record) =>
                getReportStateLabel(record.report_state) === String(value),
            render: (state: number) => {
                if (state > 4) {
                    return <Tag color="blue">Preparing for Reporting</Tag>;
                } else if (state % 2 === 1) {
                    return (
                        <Tag color="cyan">
                            {`Waiting for ${state === 1 ? "supervisor" : "client"} approval`}
                        </Tag>
                    );
                } else {
                    return (
                        <Tag color="volcano">
                            {`${state === 2 ? "Driller" : "Supervisor"} rechecking`}
                        </Tag>
                    );
                }
            },
        },
        {
            title: "Action",
            key: "action",
            dataIndex: "report_state",
            render: (_, record: DDRDisplayData) => (
                <Space size="middle">
                    <Button color="geekblue" variant="text" onClick={() => handleEditing(record)}>
                        View
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="ddr_2_approve">
            <>
                {authorityLv === "" && (
                    <div className="inaccessible_state">
                        <img className="display_img" src={notLoginState} alt="Not signed in" />
                    </div>
                )}

                {authorityLv !== "" && (
                    <div className="table-wrapper">
                        <Table<DDRDisplayData>
                            columns={columns}
                            pagination={{
                                defaultPageSize: 50,
                                showSizeChanger: true,
                                pageSizeOptions: [10, 20, 50, 100],
                                position: ['bottomCenter'],
                            }}
                            dataSource={data || []}
                        />
                    </div>
                )}
            </>
        </div>
    );
};
